import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { useComposableFilter } from '@/components/ComposableFilter';
import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import {
  PaymentOverviewFilterBar,
  type PaymentOverviewViewMode,
  type ComparisonTarget,
} from '@/features/contract-and-cost/payment';
import { getMonthsInPeriod } from '@/features/contract-and-cost/period';
import {
  PaymentStatCards,
  PaymentSummaryChart,
} from '@/features/software/contract-and-cost/components';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import ContractCostTable from './-components/ContractCostTable';
import {
  usePayrollContractCostDepartments,
  usePayrollPaymentOverviewSummary,
} from './-query';

const SearchSchema = z.object({
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
});

export const Route = createFileRoute(
  '/_staff/payroll/_layout/contract-and-cost/_contract-and-cost/payment/$period/',
)({
  component: PayrollPaymentOverviewPage,
  validateSearch: zodValidator(SearchSchema),
});

function PayrollPaymentOverviewPage() {
  const { period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.paymentOverview',
  });
  const { t: tPayrollCc } = useTranslation(defaultNS, {
    keyPrefix: 'pages.payroll.contractAndCost',
  });

  const isMonthly = search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  const [showSummary, setShowSummary] = useState(true);
  const summaryRegionId = 'payroll-payment-overview-summary-region';

  const filter = useComposableFilter<Record<string, never>>({
    filterDefs: [],
    filterState: {},
    onFilterStateChange: () => {},
  });

  const { data: summary } = usePayrollPaymentOverviewSummary(period);
  const departmentsQuery = usePayrollContractCostDepartments(
    period,
    search.freeText ?? '',
  );

  const handleViewModeChange = useCallback(
    (mode: PaymentOverviewViewMode) => {
      navigate({
        search: (prev) => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate, currentMonth],
  );

  const handleMonthChange = useCallback(
    (month: number) => {
      navigate({
        search: (prev) => ({ ...prev, month }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleComparisonTargetChange = useCallback(
    (target: ComparisonTarget) => {
      navigate({
        search: (prev) => ({ ...prev, comparisonTarget: target }),
      });
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          freeText: e.target.value || undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-12 py-5">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && (
          <VStack className="gap-5">
            <PaymentStatCards summary={summary} />
            <PaymentSummaryChart data={summary.months} months={monthOptions} />
          </VStack>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors"
          aria-expanded={showSummary}
          aria-controls={summaryRegionId}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className={cn('size-3.5')}
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <PaymentOverviewFilterBar
        viewMode={search.viewMode}
        onViewModeChange={handleViewModeChange}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={handleMonthChange}
        monthOptions={monthOptions}
        searchValue={search.freeText ?? ''}
        onSearchChange={handleSearchChange}
        filter={filter}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={handleComparisonTargetChange}
      />

      {isMonthly ? (
        <VStack className="flex-1 items-center justify-center gap-2 py-16">
          <Text variant="CardHeader" className="text-neutral-muted-on-fill">
            {tPayrollCc('monthlyNotAvailable.title')}
          </Text>
          <Text variant="pSmall" className="text-neutral-muted-on-fill">
            {tPayrollCc('monthlyNotAvailable.subtitle')}
          </Text>
        </VStack>
      ) : (
        <ContractCostTable rows={departmentsQuery.data?.departments ?? []} />
      )}
    </div>
  );
}
